import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { FormattedMessage } from 'react-intl';

import {
    CalculatorStepPanel,
    ComparisonTable,
    MessageBanner,
    Sizes,
    ElementAttributes,
    useCalculator,
    MessagePropType,
    FormComputed,
    useForm,
} from '@ratehub/base-ui';
import {
    MortgageSingleRateCard,
    getPaymentFrequencyAndDivisor,
    SCENARIO_TYPES,
    useApplyForRateCallback,
    isProviderRate,
} from '@ratehub/mtg-common';
import { can_mortgage_payment } from '@ratehub/mortgage-calculations';
import { PAYMENT_FREQUENCIES } from '@ratehub/documents/mtg-journey-document/stable';


function RefinanceStepSummary({
    stepNumber,
    title,
    ctaText,
    ctaDataName,
    ...otherProps
}) {
    const form = useForm();
    const calculator = useCalculator();
    const applyForRate = useApplyForRateCallback();

    const isVisible = calculator.hasCompletedPreviousStep(stepNumber);

    const rateFetchingOptions = {
        scenario: SCENARIO_TYPES.REFINANCE,
        amortization: form.values?.amortization,
        city: form.values?.city,
        term: form.values?.term,
        type: form.values?.type,
    };

    const displayedRate = isProviderRate(form.values.selectedRate)
        ? form.values.selectedRate
        : form.values.bestRate;

    return (
        <CalculatorStepPanel
            title={title}
            ctaText={ctaText}
            ctaDataName={ctaDataName}
            stepNumber={stepNumber}
            hasCompleteButton={true}
            onConfirmComplete={() =>
                applyForRate({
                    rate: displayedRate,
                    rateFetchingOptions,
                })
            }
            isVisible={isVisible}
            {...{ [ElementAttributes.CALCULATOR_STEP_KEY]: stepNumber }}
            {...otherProps}
        >
            <FormComputed
                name="newMonthlyPayment"
                value={calculateNewMonthlyPayment}
            />

            <ComparisonTable
                className="summaryTable"
                headings={COMPARISON_TABLE_HEADINGS}
                lines={COMPARISON_TABLE_LINES}
                nubbinAlignment="end"
                tooltipLocation="bottom"
            />

            <MessageBanner
                className="banner"
                variant="success"
                message={(
                    <FormattedMessage
                        id="mtg-calculators.Refinance.Summary.messageBannerMessage"
                        defaultMessage="This calculator is for illustrative purposes only. To get a more accurate understanding of your situation
                            and the options available to you, speak to a broker. They will be able to provide you with recommendations—for free!"
                    />
                )}
                iconKey="light-bulb"
            />

            <If condition={!!displayedRate}>
                <MortgageSingleRateCard 
                    rate={displayedRate}
                    data-test-name="single-rate-card"
                />
            </If>
        </CalculatorStepPanel>
    );
}

const COMPARISON_TABLE_HEADINGS = {
    label: undefined,
    columnTwo: (
        <FormattedMessage
            id="mtg-calculators.Refinance.Summary.existingLabel"
            defaultMessage="Existing:"
        />
    ),
    columnThree: (
        <FormattedMessage
            id="mtg-calculators.Refinance.Summary.newLabel"
            defaultMessage="New:"
        />
    ),
};

const COMPARISON_TABLE_LINES = [
    {
        label: (
            <FormattedMessage
                id="mtg-calculators.Refinance.Summary.mortgageBalance"
                defaultMessage="Mortgage amount"
            />
        ),
        columnOne: 'mortgageBalance',
        columnTwo: 'newMortgageAmount',
        format: 'CAD0',
        tooltip: (
            <FormattedMessage
                id="mtg-calculators.Refinance.Summary.mortgageAmountTooltip"
                defaultMessage="Your new mortgage amount includes the additional equity you requested in Step 1 of the calculator."
            />
        ),
        tooltipVariant: 'content',
    },
    {
        label: (
            <FormattedMessage
                id="mtg-calculators.Refinance.Summary.additionalEquity"
                defaultMessage="Additional equity"
            />
        ),
        columnOne: 0,
        columnTwo: ({ additionalEquity, breakagePenalty }) =>
            additionalEquity ? additionalEquity - (breakagePenalty ?? 0) : null,
        format: 'CAD0',
    },
    {
        label: (
            <FormattedMessage
                id="mtg-calculators.Refinance.Summary.penaltyFee"
                defaultMessage="Penalty"
            />
        ),
        columnOne: 0,
        columnTwo: 'breakagePenalty',
        format: 'CAD0',
        tooltip: (
            <FormattedMessage
                id="mtg-calculators.Refinance.Summary.penaltyFeeTooltip"
                defaultMessage="Many Canadians will use the additional equity they receive in order to pay for the penalty fee they’ve incurred by breaking their mortgage.
                    Therefore, we have allocated part of the additional equity requested in Step 1 to the penalty fee. "
            />
        ),
        tooltipVariant: 'content',
    },
    {
        label: (
            <FormattedMessage
                id="mtg-calculators.Refinance.Summary.mortgageRate"
                defaultMessage="Mortgage rate"
            />
        ),
        columnOne: ({ currentRate }) => currentRate / 100 ?? '-',
        columnTwo: ({ newMortgageRate }) => newMortgageRate,
        format: 'percent2',
    },
    {
        label: (
            <FormattedMessage
                id="mtg-calculators.Refinance.Summary.amortization"
                defaultMessage="Amortization (years)"
            />
        ),
        columnOne: '-',
        columnTwo: ({ amortization }) => amortization,
    },
    {
        label: (
            <FormattedMessage
                id="mtg-calculators.Refinance.Summary.monthlyPayment"
                defaultMessage="Monthly payment"
            />
        ),
        columnOne: ({ mortgagePayment, paymentFrequency }) => {
            const { frequency, divisor } = getPaymentFrequencyAndDivisor(paymentFrequency);

            // We always want to display their current payment as monthly
            return convertToMonthlyPayment(mortgagePayment, frequency, divisor);
        },
        columnTwo: 'newMonthlyPayment' ?? 0,
        format: 'CAD0',
        tooltip: (
            <FormattedMessage
                id="mtg-calculators.Refinance.Summary.monthlyPaymentTooltip"
                defaultMessage="We assumed that you will use the additional equity to pay for the penalty fee you’ve incurred by breaking your mortgage.
                    Therefore, the penalty fee has been baked into your monthly mortgage payment."
            />
        ),
        tooltipVariant: 'content',
    },
];

RefinanceStepSummary.propTypes = {
    title: MessagePropType.isRequired,
    ctaText: MessagePropType.isRequired,
    ctaDataName: PropTypes.string.isRequired,
    stepNumber: PropTypes.number.isRequired,
};

function calculateNewMonthlyPayment({
    newMortgageAmount,
    newMortgageRate,
    amortization,
    paymentFrequency,
}) {
    const { frequency, divisor } = getPaymentFrequencyAndDivisor(
        paymentFrequency ?? PAYMENT_FREQUENCIES.MONTHLY,
    );

    const payment = can_mortgage_payment(
        newMortgageAmount,
        newMortgageRate,
        amortization,
        frequency,
        divisor,
    ) || 0;

    return payment
        ? convertToMonthlyPayment(payment, frequency, divisor)
        : 0;
}

function convertToMonthlyPayment(payment, frequency, divisor) {
    return (payment * (frequency * divisor)) / 12;
}

const StyledRefinanceStepSummary = styled(observer(RefinanceStepSummary))`
    > .summaryTable,
    > .banner {
        margin-bottom: ${Sizes.SPACING.TWO};
    }
`;

export default StyledRefinanceStepSummary;
